import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Right-side context drawer (spec §6.3). Selections on grid pages open
 * one of these instead of a modal — keeps the list scrollable +
 * preserves the user's row position. Built on Radix Dialog primitives
 * with custom slide-from-right animation.
 *
 * Width adapts to viewport: 480px on desktop, full-screen on mobile.
 */
export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const widths = {
    sm: 'w-full sm:max-w-md',
    md: 'w-full sm:max-w-xl',
    lg: 'w-full sm:max-w-3xl',
  } as const;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-ink-900/30 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed right-0 top-0 z-50 h-full bg-white dark:bg-ink-900',
            'border-l border-ink-200 dark:border-ink-800 shadow-2xl outline-none',
            'flex flex-col',
            widths[size],
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-1/2',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right-1/2',
          )}
        >
          <div className="px-5 py-4 border-b border-ink-200 dark:border-ink-800 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <DialogPrimitive.Title className="font-medium text-base truncate">{title}</DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-xs text-ink-500 mt-0.5 truncate">{description}</DialogPrimitive.Description>
              ) : null}
            </div>
            <DialogPrimitive.Close
              className="p-1 rounded-md text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800 shrink-0"
              aria-label="Close"
            >
              <X size={16} />
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

          {footer ? (
            <div className="px-5 py-3 border-t border-ink-200 dark:border-ink-800 bg-ink-50 dark:bg-ink-900/50">
              {footer}
            </div>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
